import { SIDER_WIDTH_STORAGE_KEY } from '@zhiyue/core';
import React, { useEffect, useRef, useState } from 'react';
import './shell.less';

export { SIDER_WIDTH_STORAGE_KEY };
export const DEFAULT_SIDER_WIDTH = 140;
export const MIN_SIDER_WIDTH = 120;
export const MAX_SIDER_WIDTH = 173;
const WIDTH_MIGRATION_KEY = 'crm:sider-width-reduced:v1';
export const clampSiderWidth = (value: number) =>
  Math.round(Math.max(MIN_SIDER_WIDTH, Math.min(MAX_SIDER_WIDTH, value)));
export function getStoredSiderWidth() {
  try {
    const raw = localStorage.getItem(SIDER_WIDTH_STORAGE_KEY);
    const shouldMigrate = localStorage.getItem(WIDTH_MIGRATION_KEY) !== '1';
    localStorage.setItem(WIDTH_MIGRATION_KEY, '1');
    if (raw === null || raw.trim() === '') return DEFAULT_SIDER_WIDTH;
    const value = Number(raw);
    if (!Number.isFinite(value)) return DEFAULT_SIDER_WIDTH;
    // Scale existing preferences once; subsequent reads retain the chosen width.
    const width = clampSiderWidth(shouldMigrate ? (value * 2) / 3 : value);
    if (value !== width)
      localStorage.setItem(SIDER_WIDTH_STORAGE_KEY, String(width));
    return width;
  } catch {
    return DEFAULT_SIDER_WIDTH;
  }
}
export default function SiderResizeHandle({
  enabled,
  siderWidth,
  onResizeWidth,
}: {
  enabled: boolean;
  siderWidth: number;
  onResizeWidth: (width: number) => void;
}) {
  const [bounds, setBounds] = useState<{ left: number; top: number }>();
  const widthRef = useRef(siderWidth);
  useEffect(() => {
    widthRef.current = siderWidth;
  }, [siderWidth]);
  const cleanup = useRef<(() => void) | undefined>(undefined);
  useEffect(() => {
    if (!enabled) {
      setBounds(undefined);
      return;
    }
    let sider: HTMLElement | null = null;
    const measure = () => {
      if (!sider) {
        setBounds(undefined);
        return;
      }
      const box = sider.getBoundingClientRect();
      const header = document
        .querySelector('.ant-pro-layout-header')
        ?.getBoundingClientRect();
      const trigger = sider
        .querySelector('.ant-pro-sider-collapsed-button')
        ?.getBoundingClientRect();
      const next =
        window.innerWidth > 768 && box.width > 90
          ? {
              left: box.right,
              top: Math.max(
                header?.bottom || 44,
                trigger ? trigger.bottom + 12 : 56,
              ),
            }
          : undefined;
      setBounds((previous) =>
        previous?.left === next?.left && previous?.top === next?.top
          ? previous
          : next,
      );
    };
    const observer = new ResizeObserver(measure);
    const syncSider = () => {
      const next = document.querySelector<HTMLElement>('.ant-pro-sider');
      if (next === sider) return;
      observer.disconnect();
      sider = next;
      if (sider) observer.observe(sider);
      measure();
    };
    // ProLayout can create its sider only after the session/permissions resolve.
    const mounting = new MutationObserver(syncSider);
    mounting.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('resize', measure);
    syncSider();
    return () => {
      mounting.disconnect();
      observer.disconnect();
      window.removeEventListener('resize', measure);
      cleanup.current?.();
    };
  }, [enabled]);
  const commit = (value: number) => {
    const width = clampSiderWidth(value);
    widthRef.current = width;
    onResizeWidth(width);
    try {
      localStorage.setItem(SIDER_WIDTH_STORAGE_KEY, String(width));
    } catch {
      /* Storage is optional. */
    }
  };
  if (!bounds) return null;
  return (
    <hr
      className="crm-sider-resize"
      aria-label="调整导航宽度"
      aria-orientation="vertical"
      aria-valuemin={MIN_SIDER_WIDTH}
      aria-valuemax={MAX_SIDER_WIDTH}
      aria-valuenow={siderWidth}
      tabIndex={0}
      style={{
        left: bounds.left - 6,
        top: bounds.top,
        height: `calc(100dvh - ${bounds.top}px)`,
      }}
      onKeyDown={(event) => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        commit(
          widthRef.current +
            (event.key === 'ArrowRight' ? 1 : -1) * (event.shiftKey ? 24 : 8),
        );
      }}
      onPointerDown={(event) => {
        if (event.button !== 0) return;
        event.preventDefault();
        cleanup.current?.();
        const startX = event.clientX,
          startWidth = widthRef.current;
        const cursor = document.body.style.cursor,
          selection = document.body.style.userSelect;
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
        event.currentTarget.dataset.dragging = 'true';
        const handle = event.currentTarget;
        const move = (next: PointerEvent) =>
          commit(startWidth + next.clientX - startX);
        const stop = (next?: PointerEvent) => {
          if (next?.type === 'pointerup') move(next);
          delete handle.dataset.dragging;
          document.body.style.cursor = cursor;
          document.body.style.userSelect = selection;
          window.removeEventListener('pointermove', move);
          window.removeEventListener('pointerup', stop);
          window.removeEventListener('pointercancel', stop);
          cleanup.current = undefined;
        };
        cleanup.current = stop;
        window.addEventListener('pointermove', move);
        window.addEventListener('pointerup', stop);
        window.addEventListener('pointercancel', stop);
      }}
    />
  );
}
